import {
  useEffect,
  useRef,
  useCallback,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

interface PopoverProps {
  isOpen: boolean;
  onToggle: () => void;
  trigger: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  className?: string;
}

interface Coords {
  top: number;
  left: number;
}

function computeCoords(
  triggerRect: DOMRect,
  side: string,
  align: string,
  dropdownRect?: DOMRect,
): Coords {
  const gap = 8;
  let top = 0;
  let left = 0;

  const dw = dropdownRect?.width ?? 0;
  const dh = dropdownRect?.height ?? 0;

  switch (side) {
    case "bottom":
      top = triggerRect.bottom + gap;
      break;
    case "top":
      top = triggerRect.top - dh - gap;
      break;
    case "left":
      left = triggerRect.left - dw - gap;
      top = triggerRect.top;
      break;
    case "right":
      left = triggerRect.right + gap;
      top = triggerRect.top;
      break;
  }

  if (side === "top" || side === "bottom") {
    switch (align) {
      case "start":
        left = triggerRect.left;
        break;
      case "center":
        left = triggerRect.left + triggerRect.width / 2 - dw / 2;
        break;
      case "end":
        left = triggerRect.right - dw;
        break;
    }
  }

  if (side === "left" || side === "right") {
    switch (align) {
      case "start":
        top = triggerRect.top;
        break;
      case "center":
        top = triggerRect.top + triggerRect.height / 2 - dh / 2;
        break;
      case "end":
        top = triggerRect.bottom - dh;
        break;
    }
  }

  return { top, left };
}

export function Popover({
  isOpen,
  onToggle,
  trigger,
  children,
  side = "top",
  align = "start",
  className = "",
}: PopoverProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<Coords>({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const dropdownRect = dropdownRef.current?.getBoundingClientRect();
    setCoords(computeCoords(triggerRect, side, align, dropdownRect));
  }, [side, align]);

  // Position on open and reposition on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    // Initial position (without dropdown dimensions)
    updatePosition();

    // Re-position after dropdown renders (now with real dimensions)
    const raf = requestAnimationFrame(() => updatePosition());

    const handleReposition = () => updatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [isOpen, updatePosition]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        onToggle();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onToggle]);

  return (
    <div className="relative inline-flex">
      <div
        ref={triggerRef}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        role="button"
        tabIndex={0}
        className="cursor-pointer"
      >
        {trigger}
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
            }}
            className={`z-[9999] rounded-xl border border-border bg-popover text-popover-foreground shadow-lg ${className}`}
          >
            {children}
          </div>,
          document.body,
        )}
    </div>
  );
}
